import type { ReactNode, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import cc from 'classcat'

import { useStickyHeaderContext } from '../../utils/StickyHeaderContext'

export const useIsToolbarSticky = (): [RefObject<HTMLDivElement | null>, boolean] => {
  const { headerRef, isStickyHeaderShown } = useStickyHeaderContext() ?? {}
  const ref = useRef<HTMLDivElement>(null)
  const [isToolbarSticky, setIsToolbarSticky] = useState(false)

  // Observe marker element to detect whether it has been scrolled out of view
  // via the upper edge of the viewport, or above the bottom edge of
  // the sticky header (when a sticky header is enabled).
  // In both cases the toolbar should become sticky:
  //  - to the top of the viewport in the first case
  //  - or to the bottom of the sticky header in the second case
  useEffect(() => {
    const markerElement = ref.current
    if (!markerElement) return

    const effectiveHeight = isStickyHeaderShown ? (headerRef?.current?.offsetHeight ?? 0) : 0

    // The observer is recreated when isStickyHeaderShown changes (e.g. header
    // hides during revealOnScrollUp scroll-down). On recreation the new observer
    // fires an initial callback immediately. If the marker is in the
    // [0, oldEffectiveHeight] zone at that instant, the new (smaller)
    // effectiveHeight would report false and briefly un-stick the toolbar.
    // To prevent this flicker we only apply the initial callback when it says
    // sticky=true. False is skipped — the previous state is preserved — and
    // subsequent scroll-triggered callbacks (non-initial) always apply.
    let isFirstCallback = true

    const observer = new IntersectionObserver(
      (entries) => {
        const top = entries[0]?.boundingClientRect.top ?? 0
        const newSticky = top < effectiveHeight

        if (isFirstCallback) {
          isFirstCallback = false
          if (newSticky) setIsToolbarSticky(true)
          return
        }

        setIsToolbarSticky(newSticky)
      },
      { rootMargin: `${-effectiveHeight}px 0px 0px 0px` },
    )

    observer.observe(markerElement)

    return () => {
      observer?.unobserve(markerElement)
    }
  }, [isStickyHeaderShown, headerRef])

  return [ref, isToolbarSticky]
}

type Props = Readonly<{
  onScrollIntoView: (inView: boolean) => void
  children?: ReactNode
  className?: string
}>
export default function ToolbarTop({ onScrollIntoView, children, className }: Props) {
  const [refTopbar, isToolbarSticky] = useIsToolbarSticky()

  useEffect(() => {
    onScrollIntoView(!isToolbarSticky)
  }, [isToolbarSticky]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div ref={refTopbar} />
      {children && (
        <div
          className={cc({
            [`${className}-sticky`]: isToolbarSticky,
          })}
        >
          <div className={className}>{children}</div>
        </div>
      )}
    </>
  )
}
